import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Search, Briefcase, Clock, Wrench, CheckCircle, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = { new_lead: "New Lead", scheduled: "Scheduled", in_progress: "In Progress", completed: "Completed" };
const STATUS_COLORS = { new_lead: "#3B82F6", scheduled: "#F59E0B", in_progress: "#D95D39", completed: "#16A34A" };
const STATUS_ICONS = { new_lead: Briefcase, scheduled: Clock, in_progress: Wrench, completed: CheckCircle };
const PIPELINE = ["new_lead", "scheduled", "in_progress", "completed"];

export default function TrackJobPage() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Track Your Job | Double D Trapping LLC";
  }, []);

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) { toast.error("Enter your name or phone number"); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/track?q=${encodeURIComponent(query.trim())}`);
      setResults(res.data);
      if (res.data.length === 0) toast.info("No jobs found. Check your spelling or try your phone number.");
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <>
      {/* Header */}
      <section className="bg-[#0F1A15] border-b border-[#1F2937] py-16 sm:py-20" data-testid="track-page-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-[#D95D39] uppercase text-xs font-bold tracking-[0.2em] mb-4">Job Tracker</p>
          <h1 className="text-[#F8FAFC] font-black text-4xl sm:text-5xl tracking-tighter mb-4" data-testid="track-page-title">Track Your Job</h1>
          <p className="text-[#9CA3AF] text-base sm:text-lg max-w-2xl leading-relaxed">Enter your name or phone number to see the current status of your job.</p>
        </div>
      </section>

      {/* Search */}
      <section className="bg-[#0B110E] py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <form onSubmit={search} className="flex gap-3 mb-10" data-testid="track-search-form">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <Input data-testid="track-search-input" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Your name or phone number" className="bg-[#0F1A15] border-[#1F2937] text-[#F8FAFC] pl-10 h-12 rounded-sm focus-visible:ring-[#D95D39]" />
            </div>
            <button type="submit" disabled={loading} data-testid="track-search-btn" className="bg-[#D95D39] hover:bg-[#c04d2d] disabled:opacity-50 text-white font-bold uppercase tracking-widest text-sm px-6 h-12 rounded-sm transition-colors duration-150">
              {loading ? "..." : "Search"}
            </button>
          </form>

          {/* Results */}
          {results !== null && (
            <div data-testid="track-results">
              {results.length === 0 ? (
                <div className="text-center py-12 border border-[#1F2937] rounded-sm bg-[#0F1A15]">
                  <p className="text-[#9CA3AF] text-sm mb-4">No jobs found matching your search.</p>
                  <p className="text-[#64748B] text-xs">Try a different name or phone number, or call us at <a href="tel:9042376253" className="text-[#D95D39] font-bold">904-237-6253</a>.</p>
                </div>
              ) : results.map((job) => (
                <div key={job.id} className="border border-[#1F2937] rounded-sm p-6 bg-[#0F1A15] mb-4" data-testid={`track-result-${job.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-[#F8FAFC] font-bold text-lg">{job.animal_type}</p>
                      <p className="text-[#9CA3AF] text-sm">{job.address}</p>
                      <p className="text-[#64748B] text-xs mt-1">Submitted {new Date(job.created_at).toLocaleDateString()}</p>
                    </div>
                    <span className="text-sm font-bold px-3 py-1.5 rounded-sm" style={{ backgroundColor: STATUS_COLORS[job.status] + "15", color: STATUS_COLORS[job.status] }}>
                      {STATUS_LABELS[job.status]}
                    </span>
                  </div>
                  {/* Status Pipeline Visual */}
                  <div className="flex items-center gap-1 mt-4" data-testid={`pipeline-${job.id}`}>
                    {PIPELINE.map((s, i) => {
                      const reached = PIPELINE.indexOf(job.status) >= i;
                      const Icon = STATUS_ICONS[s];
                      return (
                        <div key={s} className="flex items-center flex-1">
                          <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-sm text-xs font-semibold w-full justify-center ${reached ? "" : "opacity-30"}`} style={{ backgroundColor: reached ? STATUS_COLORS[s] + "15" : "#1E293B", color: reached ? STATUS_COLORS[s] : "#64748B" }}>
                            <Icon className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">{STATUS_LABELS[s]}</span>
                          </div>
                          {i < 3 && <div className={`w-4 h-0.5 shrink-0 ${reached && PIPELINE.indexOf(job.status) > i ? "bg-[#374151]" : "bg-[#1E293B]"}`} />}
                        </div>
                      );
                    })}
                  </div>
                  {job.scheduled_date && (
                    <p className="text-[#9CA3AF] text-sm mt-3">Scheduled: <span className="text-[#F8FAFC] font-semibold">{job.scheduled_date}</span></p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-8">
            <p className="text-[#64748B] text-xs">Questions? Call <a href="tel:9042376253" className="text-[#D95D39] font-bold">904-237-6253</a> and D. Davis will update you directly.</p>
          </div>
        </div>
      </section>
    </>
  );
}
